import React from "react";
import { ShieldCheck, ShieldWarning, Lock, Prohibit, TrendUp, Sliders, PencilSimple, ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { TID } from "@/constants/testIds";
import { countSubstantiveTouches, MIN_TOUCHES } from "@/lib/compliance";

// Re-exported so existing consumers (Optimizer.jsx) that import from this
// module continue to work. The authoritative implementation lives in
// @/lib/compliance to eliminate rule drift between SundayAutoMode /
// PortfolioSplit / ComplianceBanner (previously the Portfolio Split's
// mount-time counter was missing 2 of the 6 touch types).
export { countSubstantiveTouches, MIN_TOUCHES };

/**
 * DFS Community Fair-Play compliance banner.
 * Counts substantive user overrides (locks, excludes, projection edits, exposure caps)
 * and blocks generation until >= MIN_TOUCHES independent decisions are made.
 *
 * Kept in sync with backend `compliance.py::MIN_TOUCHES` via @/lib/compliance.
 */

export default function ComplianceBanner({ overrides, className = "" }) {
  const touches = countSubstantiveTouches(overrides);
  const compliant = touches >= MIN_TOUCHES;
  const Icon = compliant ? ShieldCheck : ShieldWarning;
  const color = compliant ? "#39FF14" : "#FFCC00";

  return (
    <div
      data-testid={TID.complianceBanner}
      className={`bg-[#121212] border-l-4 border border-zinc-800 ${className}`}
      style={{ borderLeftColor: color }}
    >
      <div className="px-4 py-3 flex items-start gap-3">
        <Icon size={22} weight="fill" style={{ color, flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="overline">DFS Fair-Play Compliance</div>
            <div
              data-testid={TID.complianceTouchCount}
              className={`text-[10px] mono px-2 py-0.5 border ${
                compliant ? "border-[#39FF14] text-[#39FF14]" : "border-[#FFCC00] text-[#FFCC00]"
              }`}
            >
              {touches} / {MIN_TOUCHES} TOUCHES
            </div>
          </div>
          {compliant ? (
            <p className="text-xs text-zinc-400 mt-1.5">
              Custom build detected — generation unlocked.
            </p>
          ) : (
            <p className="text-xs text-zinc-400 mt-1.5">
              To comply with DFS community fair-play guidelines, please adjust at least{" "}
              <span className="text-[#FFCC00] font-bold">{MIN_TOUCHES}</span> player projections,
              exposures, locks, or excludes to customize your build before generating lineups.
            </p>
          )}
          <div className="flex items-center gap-3 mt-2 flex-wrap text-[10px] mono text-zinc-500">
            <span className="inline-flex items-center gap-1"><Lock size={10} /> Lock</span>
            <span className="inline-flex items-center gap-1"><Prohibit size={10} /> Exclude</span>
            <span className="inline-flex items-center gap-1"><PencilSimple size={10} /> Edit Proj</span>
            <span className="inline-flex items-center gap-1"><TrendUp size={10} /> Boost %</span>
            <span className="inline-flex items-center gap-1"><ArrowDown size={10} /> Min Exp</span>
            <span className="inline-flex items-center gap-1"><ArrowUp size={10} /> Max Exp</span>
          </div>
        </div>
      </div>
    </div>
  );
}
